/**
 * 导航翻译 - 意译风格
 * TopNav, Sidebar, 底部导航
 */

import type { TranslationDict } from '../types';

export const navigation: TranslationDict = {
  account: {
    en: 'Account',
    'zh-CN': '账户',
    ja: 'アカウント',
  },

  // 主导航
  main: {
    home: {
      en: 'Home',
      'zh-CN': '首页',
      ja: 'ホーム',
    },
    explore: {
      en: 'Explore',
      'zh-CN': '发现',
      ja: '発見',
    },
    feed: {
      en: 'Feed',
      'zh-CN': '动态',
      ja: 'フィード',
    },
    messages: {
      en: 'Messages',
      'zh-CN': '消息',
      ja: 'メッセージ',
    },
    notifications: {
      en: 'Notifications',
      'zh-CN': '通知',
      ja: '通知',
    },
    circles: {
      en: 'Circles',
      'zh-CN': '社区',
      ja: 'サークル',
    },
    search: {
      en: 'Search',
      'zh-CN': '搜索',
      ja: '検索',
    },
    profile: {
      en: 'Profile',
      'zh-CN': '我的',
      ja: 'プロフィール',
    },
  },
  
  // Agent 模块导航
  agent: {
    plaza: {
      en: 'Home',
      'zh-CN': '首页',
      ja: 'ホーム',
    },
    discovery: {
      en: 'Plaza',
      'zh-CN': '广场',
      ja: 'プラザ',
    },
    myAgent: {
      en: 'My AI',
      'zh-CN': '我的 AI',
      ja: 'マイAI',
    },
    chat: {
      en: 'Chat',
      'zh-CN': '对话',
      ja: 'チャット',
    },
  },
  
  // TopicFeed 相关
  topicfeed: {
    shorts: {
      en: 'Shorts',
      'zh-CN': '短视频',
      ja: 'ショート',
    },
    cinema: {
      en: 'Cinema',
      'zh-CN': '影院',
      ja: 'シネマ',
    },
    reader: {
      en: 'Reader',
      'zh-CN': '阅读',
      ja: 'リーダー',
    },
    enterTopic: {
      en: 'Enter Topic',
      'zh-CN': '进入话题',
      ja: 'トピックへ',
    },
  },
  
  // 搜索相关
  search: {
    placeholder: {
      en: 'Search posts, people, topics...',
      'zh-CN': '搜索帖子、用户、话题...',
      ja: '投稿、ユーザー、トピックを検索...',
    },
    placeholderShort: {
      en: 'Search...',
      'zh-CN': '搜索...',
      ja: '検索...',
    },
    recent: {
      en: 'Recent searches',
      'zh-CN': '最近搜索',
      ja: '最近の検索',
    },
    clear: {
      en: 'Clear',
      'zh-CN': '清空',
      ja: 'クリア',
    },
    trending: {
      en: 'Trending',
      'zh-CN': '热门搜索',
      ja: 'トレンド',
    },
  },
  
  // 抽屉菜单 — 部分复用 main.* 和 agent.*，这里放独有的
  drawer: {
    myProfile: {
      en: 'My Profile',
      'zh-CN': '我的主页',
      ja: 'マイページ',
    },
    myAgent: {
      en: 'My Agent',
      'zh-CN': '我的 Agent',
      ja: 'マイエージェント',
    },
    aiExplore: {
      en: 'AI Explore',
      'zh-CN': 'AI 探索',
      ja: 'AI 探索',
    },
    bookmarks: {
      en: 'Bookmarks',
      'zh-CN': '收藏',
      ja: 'ブックマーク',
    },
    membership: {
      en: 'Membership',
      'zh-CN': '会员',
      ja: 'メンバーシップ',
    },
  },

  // 底部导航 — 与 main.* 有差异的项
  bottomNav: {
    discover: {
      en: 'Discover',
      'zh-CN': '发现',
      ja: '発見',
    },
    alerts: {
      en: 'Alerts',
      'zh-CN': '通知',
      ja: '通知',
    },
  },

  // 外观 / 安装
  appearance: {
    themeColor: {
      en: 'Theme Color',
      'zh-CN': '主题色',
      ja: 'テーマカラー',
    },
    lightMode: {
      en: 'Light Mode',
      'zh-CN': '浅色模式',
      ja: 'ライトモード',
    },
    darkMode: {
      en: 'Dark Mode',
      'zh-CN': '深色模式',
      ja: 'ダークモード',
    },
    installApp: {
      en: 'Install App',
      'zh-CN': '安装应用',
      ja: 'アプリをインストール',
    },
    iosInstallGuide: {
      en: 'Tap the share button ↑ in Safari, then "Add to Home Screen"',
      'zh-CN': '在 Safari 中点击分享按钮 ↑，然后选择"添加到主屏幕"',
      ja: 'Safariの共有ボタン ↑ をタップし、「ホーム画面に追加」を選択',
    },
    bannerTitle: {
      en: 'Install Kommend',
      'zh-CN': '安装 Kommend',
      ja: 'Kommend をインストール',
    },
    bannerDescription: {
      en: 'Add to home screen for a better experience',
      'zh-CN': '添加到主屏幕，体验更流畅',
      ja: 'ホーム画面に追加して快適に利用',
    },
    install: {
      en: 'Install',
      'zh-CN': '安装',
      ja: 'インストール',
    },
    addToHomeScreen: {
      en: 'Add to Home Screen',
      'zh-CN': '添加到主屏幕',
      ja: 'ホーム画面に追加',
    },
    updateAvailable: {
      en: 'Update available',
      'zh-CN': '有新版本可用',
      ja: 'アップデートがあります',
    },
    reload: {
      en: 'Reload',
      'zh-CN': '刷新',
      ja: 'リロード',
    },
  },

  // 用户菜单
  userMenu: {
    settings: {
      en: 'Settings',
      'zh-CN': '设置',
      ja: '設定',
    },
    help: {
      en: 'Help Center',
      'zh-CN': '帮助中心',
      ja: 'ヘルプ',
    },
    language: {
      en: 'Language',
      'zh-CN': '语言',
      ja: '言語',
    },
    theme: {
      en: 'Appearance',
      'zh-CN': '外观',
      ja: '表示',
    },
    logout: {
      en: 'Sign out',
      'zh-CN': '退出登录',
      ja: 'ログアウト',
    },
    anonymous: {
      en: 'Guest Mode',
      'zh-CN': '游客模式',
      ja: 'ゲストモード',
    },
    login: {
      en: 'Sign in',
      'zh-CN': '登录',
      ja: 'ログイン',
    },
    loginSignup: {
      en: 'Log in / Sign up',
      'zh-CN': '登录 / 注册',
      ja: 'ログイン / 登録',
    },
  },
};
